import React from "react";
import { useBranding, absoluteUrl } from "../context/BrandingContext";

/**
 * Renders the app brand mark — uploaded logo image when configured,
 * otherwise a square text-mark with the first letter of app_name.
 *
 * Props:
 *  - size: "sm" | "md" | "lg"  (default md)
 *  - showName: boolean — also render the app_name beside the mark (default true)
 *  - className: extra classes for the wrapper
 */
export default function BrandLogo({ size = "md", showName = true, className = "" }) {
  const branding = useBranding();
  const logo = absoluteUrl(branding.logo_url);
  const name = branding.app_name || "Faro";

  const boxSize = {
    sm: "h-6 w-6 text-xs",
    md: "h-7 w-7 text-sm",
    lg: "h-[52px] w-[52px] text-base",
    xl: "h-20 w-20 text-lg",
  }[size] || "h-7 w-7 text-sm";
  const nameSize = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-xl",
    xl: "text-2xl",
  }[size] || "text-base";

  // For images, allow horizontal logos to keep their natural aspect ratio:
  // constrain by height only and let width flow.
  const imgSize = {
    sm: "h-6",
    md: "h-7",
    lg: "h-[52px]",
    xl: "h-20",
  }[size] || "h-7";

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="brand-logo">
      {logo ? (
        <img
          src={logo}
          alt={name}
          className={`${imgSize} w-auto object-contain rounded-sm`}
          data-testid="brand-logo-img"
        />
      ) : (
        <div
          className={`${boxSize} bg-foreground text-background grid place-items-center font-display font-black tracking-tighter rounded-sm`}
          data-testid="brand-logo-fallback"
        >
          {name.charAt(0).toUpperCase()}
        </div>
      )}
      {showName && <span className={`font-display font-bold tracking-tight ${nameSize}`} data-testid="brand-name">{name}</span>}
    </div>
  );
}
